import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAPIContext } from '../../context/api';
import { VideoAPI } from '../../../../constructs/video-processing/interface';
import { PosterAPI } from '../../../../constructs/poster-processing/interface';

export const useUploadMedia = () => {
  const { callEndpoint } = useAPIContext();

  const uploadVideoDirect = React.useCallback(
    async (fileType: 'video', fileExtension: string, file: File, clientId: string) => {
      try {
        if (!file || !file.type) {
          throw new Error('Archivo no válido o tipo de archivo no especificado.');
        }

        if (fileExtension.toLowerCase() !== '.mp4') {
          throw new Error('Solo se permiten archivos MP4 para brands.');
        }

        const fileName = uuidv4();
        const path = `/video/direct/${fileName}`;

        const response = await callEndpoint<VideoAPI, 'post'>('upload', path, 'POST', {
          fileType,
          fileExtension
        });

        const { uploadUrl, fileKey } = response;

        const s3Response = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type
          }
        });

        if (!s3Response.ok) {
          const errorText = await s3Response.text();
          throw new Error(`Error subiendo archivo a S3: ${s3Response.status} - ${errorText}`);
        }

        const publicUrl = `${process.env.REACT_APP_MEDIA_DOMAIN}/${clientId}/${fileName}/video-mp4/video.mp4`;

        return {
          fileKey,
          fileName,
          path,
          publicUrl
        };
      } catch (error) {
        console.error('Hook | Error al subir el video:', error);
        throw error;
      }
    },
    [callEndpoint]
  );

  const uploadPosterDirect = React.useCallback(
    async (fileType: 'image', fileExtension: string, file: File, fileName: string, clientId: string) => {
      try {
        if (!file || !file.type) {
          throw new Error('Archivo no válido o tipo de archivo no especificado.');
        }

        // Validar que solo sea jpg/png en el frontend
        if (!['.jpg', '.jpeg', '.png'].includes(fileExtension.toLowerCase())) {
          throw new Error('Solo se permiten archivos JPG y PNG para brands.');
        }

        const path = `/direct/${fileName}`;

        const response = await callEndpoint<PosterAPI, 'post'>('upload', path, 'POST', {
          fileType,
          fileExtension
        });

        const { uploadUrl, fileKey } = response;

        const s3Response = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type
          }
        });

        if (!s3Response.ok) {
          const errorText = await s3Response.text();
          throw new Error(`Error subiendo archivo a S3: ${s3Response.status} - ${errorText}`);
        }

        // Frontend construye la URL pública
        const publicUrl = `${process.env.REACT_APP_MEDIA_DOMAIN}/${clientId}/${fileName}/image-jpeg/${fileName}${fileExtension}`;

        return {
          fileKey,
          fileName,
          path,
          publicUrl
        };
      } catch (error) {
        console.error('Hook | Error al subir el poster:', error);
        throw error;
      }
    },
    [callEndpoint]
  );

  return { uploadVideoDirect, uploadPosterDirect };
};
